import { DocxPreviewer } from './docxPreviewer';
import { PdfPreviewer } from './pdfPreviewer';

type PreviewElements = {
  preview: HTMLElement;
  clearButton: HTMLElement | null;
  img: HTMLImageElement | null;
  pdfFrame: HTMLEmbedElement | null;
  docxContainer: HTMLElement | null;
  docxContent: HTMLElement | null;
  docxStyles: HTMLElement | null;
  nameElem: HTMLElement | null;
};

export class FilePreviewManager {
  private pdfPreviewer = new PdfPreviewer();
  private docxPreviewer = new DocxPreviewer();

  // eslint-disable-next-line complexity
  public async updateFromInput(elem: HTMLInputElement): Promise<void> {
    const elements = this.getElements(elem);
    if (!elements) {
      return;
    }
    const {
      clearButton,
      img,
      pdfFrame,
      docxContainer,
      docxContent,
      docxStyles,
      nameElem,
    } = elements;
    const fileContent = elem.dataset.fileContent || '';
    const fileName = elem.dataset.fileName || '';
    let previewUrl = elem.dataset.filePreviewUrl || '';
    let previewType = elem.dataset.filePreviewType || '';

    const placeholder =
      img?.dataset.placeholder || img?.getAttribute('data-placeholder') || '';
    if (!fileContent && !previewUrl) {
      clearButton?.classList.add('file-clear-disabled');
      if (img) {
        placeholder ? (img.src = placeholder) : img.removeAttribute('src');
        img.style.display = 'block';
      }
      this.pdfPreviewer.clear(pdfFrame);
      this.docxPreviewer.clear(docxContainer, docxContent, docxStyles);
      if (nameElem) {
        nameElem.textContent = '';
      }
      return;
    }

    clearButton?.classList.remove('file-clear-disabled');
    if (nameElem) {
      nameElem.textContent = fileName;
    }

    const isPdf =
      previewType === 'application/pdf' ||
      fileContent.startsWith('data:application/pdf') ||
      fileName.toLowerCase().endsWith('.pdf');
    if (isPdf && !previewUrl && fileContent.startsWith('data:')) {
      const createdUrl = this.createObjectUrlFromDataUrl(fileContent);
      if (createdUrl) {
        previewUrl = createdUrl;
        previewType = 'application/pdf';
        elem.dataset.filePreviewUrl = createdUrl;
        elem.dataset.filePreviewType = 'application/pdf';
      }
    }
    if (fileContent.startsWith('data:image/')) {
      if (img) {
        img.src = fileContent;
        img.style.display = 'block';
      }
      this.pdfPreviewer.clear(pdfFrame);
      this.docxPreviewer.clear(docxContainer, docxContent, docxStyles);
      return;
    }

    if (isPdf && (previewUrl || fileContent)) {
      if (img) {
        img.style.display = 'none';
      }
      this.pdfPreviewer.show(previewUrl || fileContent, pdfFrame);
      this.docxPreviewer.clear(docxContainer, docxContent, docxStyles);
      return;
    }

    if (
      fileContent.startsWith(
        'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      )
    ) {
      if (img) {
        img.style.display = 'none';
      }
      this.pdfPreviewer.clear(pdfFrame);
      if (docxContainer) {
        const target = docxContent || docxContainer;
        await this.docxPreviewer.render(
          fileContent,
          target,
          docxStyles || undefined,
        );
        docxContainer.style.display = 'block';
        this.scaleDocxPreview(target);
      }
      return;
    }

    if (img) {
      placeholder ? (img.src = placeholder) : img.removeAttribute('src');
      img.style.display = 'block';
    }
    this.pdfPreviewer.clear(pdfFrame);
    this.docxPreviewer.clear(docxContainer, docxContent, docxStyles);
  }

  private createObjectUrlFromDataUrl(dataUrl: string): string {
    const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
    if (!match) {
      return '';
    }
    const mimeType = match[1];
    const base64Data = match[2];
    try {
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch {
      return '';
    }
  }

  // eslint-disable-next-line complexity
  public scaleDocxPreview(container: HTMLElement) {
    const applyScale = (wrapper: HTMLElement) => {
      wrapper.style.transform = 'none';
      wrapper.style.transformOrigin = 'top left';
      wrapper.style.display = 'inline-block';

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const wrapperWidth =
        wrapper.scrollWidth || wrapper.getBoundingClientRect().width;
      const wrapperHeight =
        wrapper.scrollHeight || wrapper.getBoundingClientRect().height;

      if (
        containerWidth === 0 ||
        containerHeight === 0 ||
        wrapperWidth === 0 ||
        wrapperHeight === 0
      ) {
        return false;
      }

      const scale = Math.min(
        containerWidth / wrapperWidth,
        containerHeight / wrapperHeight,
      );
      wrapper.style.transform = `scale(${scale * 0.8})`;
      wrapper.style.transformOrigin = 'top left';
      return true;
    };

    const wrapper = this.getDocxWrapper(container);
    if (wrapper && applyScale(wrapper)) {
      return;
    }

    const observer = new MutationObserver(() => {
      const nextWrapper = this.getDocxWrapper(container);
      if (!nextWrapper) {
        return;
      }
      if (applyScale(nextWrapper)) {
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  private getDocxWrapper(container: HTMLElement): HTMLElement | null {
    return (
      (container.querySelector('.docx') as HTMLElement | null) ||
      (container.querySelector('.docx-wrapper') as HTMLElement | null) ||
      (container.firstElementChild as HTMLElement | null)
    );
  }

  private getElements(elem: HTMLInputElement): PreviewElements | null {
    const code = elem.getAttribute('answer');
    if (!code) {
      return null;
    }
    const preview = document.querySelector(
      '.file-preview[data-preview-for="' + code + '"]',
    ) as HTMLElement | null;
    if (!preview) {
      return null;
    }
    const inputArea = elem.closest('.input-area');
    return {
      preview,
      clearButton: (inputArea || document).querySelector(
        '.file-clear',
      ) as HTMLElement | null,
      img: preview.querySelector('img') as HTMLImageElement | null,
      pdfFrame: preview.querySelector('embed') as HTMLEmbedElement | null,
      docxContainer: preview.querySelector(
        '.file-docx-thumb',
      ) as HTMLElement | null,
      docxContent: preview.querySelector(
        '.file-docx-content',
      ) as HTMLElement | null,
      docxStyles: preview.querySelector(
        '.file-docx-styles',
      ) as HTMLElement | null,
      nameElem: preview.querySelector('.file-name') as HTMLElement | null,
    };
  }

  private tryParseFileValue(
    value: string,
  ): { name: string; src: string } | null {
    try {
      const parsed = JSON.parse(value) as { name?: string; src?: string };
      if (parsed && typeof parsed.src === 'string') {
        return { name: parsed.name || '', src: parsed.src };
      }
      return null;
    } catch {
      return null;
    }
  }
}
